import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSFormLayout } from 'components';
import { useCustomStyles } from 'core/mui/customStyles';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import moment from 'moment';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useSalaryBaseS6202Schema } from './schema';
import TableSalaryBaseAccessoire from './table';

type Props = {
    store: any;
    onSubmit: SubmitHandler<SalaireBaseAccessorySchema>;
    actions?: JSX.Element;
};

export type SalaireBaseAccessory = {
    id: string;
    payrollDueDate: Date | null;
    startDate: Date | null;
    endDate: Date | null;
    amount: string;
    cotisation: string;
};

type SalaireBaseAccessorySchema = {
    salaryBaseS6202: {
        salaries: SalaireBaseAccessory[];
        salariesMT: SalaireBaseAccessory[];
    };
};

const FormSalaryBaseS6202: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const classes = useCustomStyles();
    const { activeStep, setStepFailed } = useArretTravailFormContext();

    const validationSchema: Yup.SchemaOf<SalaireBaseAccessorySchema> = useSalaryBaseS6202Schema(store);

    const accidentDate = store.workStoppage.infos.start;
    const nbrMonth = 1;
    const initialSalaires = Array.from({ length: nbrMonth }, (_, index) => {
        return {
            id: `${index + 1}`,
            payrollDueDate: null,
            startDate: format(startOfMonth(subMonths(new Date(accidentDate), 1)), 'yyyy-MM-dd'),
            endDate: format(endOfMonth(subMonths(new Date(accidentDate), 1)), 'yyyy-MM-dd'),
            amount: '',
            cotisation: '',
        };
    });

    const methods = useForm<SalaireBaseAccessorySchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            ...store,
            salaryBaseS6202: {
                salaries: store?.salaryBaseS6202?.salaries?.length ? store.salaryBaseS6202.salaries : initialSalaires,
                salariesMT: store?.salaryBaseS6202?.salariesMT?.length ? store.salaryBaseS6202.salariesMT : [],
            },
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = methods;

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    const {
        fields: salaries,
        append: appendSalary,
        remove: removeSalary,
    } = useFieldArray<SalaireBaseAccessorySchema, 'salaryBaseS6202.salaries', 'id'>({
        control,
        name: 'salaryBaseS6202.salaries',
        keyName: 'id',
    });

    const handleNewSalaire = () => {
        appendSalary({
            id: `${salaries.length + 1}`,
            payrollDueDate: null,
            startDate: null,
            endDate: null,
            amount: '',
            cotisation: '',
        });
    };

    const {
        fields: salariesMT,
        append: appendSalaryMT,
        remove: removeSalaryMT,
    } = useFieldArray<SalaireBaseAccessorySchema, 'salaryBaseS6202.salariesMT', 'id'>({
        control,
        name: 'salaryBaseS6202.salariesMT',
        keyName: 'id',
    });

    const handleNewSalaireMT = () => {
        appendSalaryMT({
            id: `${salariesMT.length + 1}`,
            payrollDueDate: null,
            startDate: null,
            endDate: null,
            amount: '',
            cotisation: '',
        });
    };

    useEffect(() => {
        if (salaries?.length === 0) handleNewSalaire();
        if (salariesMT?.length === 0) handleNewSalaireMT();
    }, [salaries, salariesMT]);

    console.log(errors);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center" style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} md={12}>
                        <Box display="flex" width="100%" className={classNames(classes.txtGrayDarkBold, 'mb-4')}>
                            <Box flex="1" paddingRight={2} textAlign="right">
                                Date du dernier jour de travail :
                            </Box>
                            <Box flex="1">{store.workStoppage.infos.lastDayWorked && moment(store.workStoppage.infos.lastDayWorked).format('DD/MM/YYYY')}</Box>
                            {store.workStoppage.infos.returnToWorkDate && (
                                <>
                                    <Box flex="1" paddingRight={2} textAlign="right">
                                        Date de reprise :
                                    </Box>
                                    <Box flex="1">{store.workStoppage.infos.returnToWorkDate && moment(store.workStoppage.infos.returnToWorkDate).format('DD/MM/YYYY')}</Box>
                                </>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TableSalaryBaseAccessoire
                            salaries={salaries}
                            appendSalary={handleNewSalaire}
                            removeSalary={removeSalary}
                            store={store}
                            salariesMT={salariesMT}
                            appendSalaryMT={handleNewSalaireMT}
                            removeSalaryMT={removeSalaryMT}
                        />
                    </Grid>
                </Grid>
                {errors?.salaryBaseS6202?.salaries && <Box className={classNames(classes.txtRedLightBold, 'text-center')}>{errors?.salaryBaseS6202?.salaries?.message}</Box>}
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormSalaryBaseS6202;
